/* A markdown renderer in a hundred lines, instead of a dependency.
 *
 * The same reasoning as router.tsx and i18n.ts. `react-markdown` pulls in
 * remark, unified and a dozen micromark packages; `marked` is smaller but still
 * more code than this whole module. Neither is worth it to render two documents
 * we wrote ourselves, in a subset we control.
 *
 * WHY THIS IS NOT dangerouslySetInnerHTML
 *
 * Every branch below returns React elements, so text is escaped by React and
 * there is no path from document content to markup. That matters more here than
 * in most places: the document being rendered is the security document, and a
 * renderer that could be made to inject a script would be the one bug that
 * makes it a lie. It also means the CSP needs no change -- there is no inline
 * script and no inline style to allow.
 *
 * Consequences worth knowing: raw HTML in a document renders as visible text
 * rather than as markup, which is the safe direction to fail. Nested lists,
 * reference links, footnotes and blockquotes are not supported, because the
 * documents do not use them. Add a case when a document needs one.
 */
import type { ReactNode } from 'react'

/** Only schemes a document of ours has any business linking to. A markdown
 *  link is the one place a URL reaches an href, and `javascript:` is the
 *  reason to check rather than trust the source file. */
function safeHref(url: string): string | null {
  const u = url.trim()
  if (/^https?:\/\//i.test(u)) return u
  if (/^mailto:/i.test(u)) return u
  // Relative links between documents, and in-page anchors.
  if (/^[.#/]/.test(u) && !u.includes(':')) return u
  return null
}

/* Ordered so the first match wins: code before emphasis, because a backtick
 * span may legitimately contain asterisks. */
const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  INLINE.lastIndex = 0

  while ((m = INLINE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const key = `${keyBase}-${m.index}`

    if (m[1]) {
      out.push(<code key={key}>{m[1].slice(1, -1)}</code>)
    } else if (m[2]) {
      out.push(<strong key={key}>{m[2].slice(2, -2)}</strong>)
    } else if (m[3]) {
      const cut = m[3].indexOf('](')
      const label = m[3].slice(1, cut)
      const href = safeHref(m[3].slice(cut + 2, -1))
      // A link we will not follow still has text worth reading.
      out.push(href ? <a key={key} href={href}>{label}</a> : <span key={key}>{label}</span>)
    }
    last = m.index + m[0].length
  }

  if (last < text.length) out.push(text.slice(last))
  return out
}

const isTableRule = (s: string) => /^\|[\s:|-]+\|$/.test(s.trim())

function cells(row: string): string[] {
  return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())
}

export function Markdown({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const out: ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]!

    if (!line.trim()) { i++; continue }

    // Fenced code. Collected verbatim -- this is what makes a document able to
    // show a shell command, or an HTML tag, without either being interpreted.
    if (line.startsWith('```')) {
      const body: string[] = []
      i++
      while (i < lines.length && !lines[i]!.startsWith('```')) body.push(lines[i++]!)
      i++
      out.push(<pre key={i}><code>{body.join('\n')}</code></pre>)
      continue
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      // Rendered at the level written. A page that supplies its own <h1> should
      // strip the document's title rather than have this shift every level
      // down -- shifting silently turns every `##` section into an h3, which
      // then does not match the stylesheet and reads wrong in an outline.
      const Tag = `h${heading[1]!.length}` as 'h2'
      out.push(<Tag key={i}>{inline(heading[2]!, `h${i}`)}</Tag>)
      i++
      continue
    }

    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      out.push(<hr key={i} />)
      i++
      continue
    }

    if (line.trimStart().startsWith('|') && isTableRule(lines[i + 1] ?? '')) {
      const head = cells(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && lines[i]!.trimStart().startsWith('|')) rows.push(cells(lines[i++]!))
      out.push(
        <div className="doc-table" key={`t${i}`}>
          <table>
            {/* A header row of empty cells is a layout table -- several of ours
                are -- so it is dropped rather than rendered as blank headings. */}
            {head.some(Boolean) && (
              <thead>
                <tr>{head.map((c, n) => <th key={n}>{inline(c, `th${i}-${n}`)}</th>)}</tr>
              </thead>
            )}
            <tbody>
              {rows.map((r, n) => (
                <tr key={n}>{r.map((c, k) => <td key={k}>{inline(c, `td${n}-${k}`)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    const bullet = /^\s*[-*]\s+/
    const number = /^\s*\d+\.\s+/
    for (const [re, Tag] of [[bullet, 'ul'], [number, 'ol']] as const) {
      if (!re.test(line)) continue
      const items: string[] = []
      while (i < lines.length && re.test(lines[i] ?? '')) {
        // A wrapped list item continues on the next indented, non-empty line.
        let text = lines[i]!.replace(re, '')
        i++
        while (i < lines.length && lines[i]!.trim() && !re.test(lines[i]!) &&
               /^\s+/.test(lines[i]!)) text += ' ' + lines[i++]!.trim()
        items.push(text)
      }
      out.push(
        <Tag key={`l${i}`}>
          {items.map((t, n) => <li key={n}>{inline(t, `li${i}-${n}`)}</li>)}
        </Tag>,
      )
      break
    }
    if (bullet.test(line) || number.test(line)) continue

    const para: string[] = []
    while (i < lines.length && lines[i]!.trim() && !lines[i]!.startsWith('#') &&
           !lines[i]!.startsWith('```') && !lines[i]!.trimStart().startsWith('|') &&
           !bullet.test(lines[i]!) && !number.test(lines[i]!) &&
           !/^(-{3,}|\*{3,})$/.test(lines[i]!.trim())) para.push(lines[i++]!.trim())
    out.push(<p key={`p${i}`}>{inline(para.join(' '), `p${i}`)}</p>)
  }

  return <div className="doc">{out}</div>
}
